/*
 * 消息内容渲染器
 *
 * 职责：把 AI / 用户消息的 markdown 文本渲染成丰富 UI，
 *      包括 10 种自定义代码块（与 XHeyClaw-Web 完全对齐 schema）。
 *
 * 作用域控制：
 *   - 所有样式都在 .aiClerk-md 容器内，不污染全局/Antd 3
 *   - 用 BEM-like 类名 .aiClerk-md__xxx
 *   - 不引入 Tailwind / shadcn-ui，纯 SCSS
 */

import React, { useCallback, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  PhotoListBlock,
  FileListBlock,
  EChartsBlock,
  MermaidBlock,
  MetricsBlock,
  TaskStatusBlock,
  VerifyResultBlock,
  ImageGalleryBlock,
  CronCardBlock,
  SkillCardBlock
} from './blocks'
import { CUSTOM_BLOCK_LANGS, CustomBlockLang } from './blocks/types'
import { ImagePreview } from './ImagePreview'
import './MessageContent.scss'

interface MessageContentProps {
  content: string
}

const CUSTOM_LANG_SET = new Set<string>(CUSTOM_BLOCK_LANGS)

/** 把代码块语言映射到组件 */
function renderCustomBlock(
  lang: CustomBlockLang,
  code: string
): React.ReactNode {
  switch (lang) {
    case 'photo-list':
      return <PhotoListBlock code={code} />
    case 'file-list':
      return <FileListBlock code={code} />
    case 'echarts':
      return <EChartsBlock code={code} />
    case 'mermaid':
      return <MermaidBlock code={code} />
    case 'metrics':
      return <MetricsBlock code={code} />
    case 'task-status':
      return <TaskStatusBlock code={code} />
    case 'verify-result':
      return <VerifyResultBlock code={code} />
    case 'image-gallery':
      return <ImageGalleryBlock code={code} />
    case 'cron-card':
      return <CronCardBlock code={code} />
    case 'skill-card':
      return <SkillCardBlock code={code} />
    default:
      return null
  }
}

/**
 * 预处理：连续 3+ 张图片自动转为 image-gallery 代码块
 * （对齐 XHeyClaw-Web 的 groupConsecutiveImages 逻辑）
 */
function preprocessMarkdown(text: string): string {
  if (!text) return text
  // 用 regex 匹配连续的 ![](url)（中间允许空白）
  const normalized = text
    .replace(/\[\[reply_to_current\]\]\s*/g, '')
    .replace(
      /(^|[\s\u3000])(uploads\/[^\s`]+?\.(?:xlsx?|docx?|pdf))/gm,
      (_, prefix: string, path: string) => `${prefix}\`${path}\``
    )
  return normalized.replace(/(!\[[^\]]*\]\([^)]+\)[\s\n]*){3,}/g, (match) => {
    const urls = Array.from(match.matchAll(/!\[[^\]]*\]\(([^)]+)\)/g)).map(
      (m) => m[1]
    )
    return `\n\n\`\`\`image-gallery\n${JSON.stringify(urls)}\n\`\`\`\n\n`
  })
}

// ⚡ 性能: components 配置提到模块顶层, 不要每次 render 新建 object literal.
//
// 类比 iOS: 别在 `cellForRowAt` 里 new 一个 closure 当 delegate — 那让上层每次都
// 收到不同的 instance, 触发不必要的 setup. 这里 ReactMarkdown 也是同理 — 每次
// render 给它一个新 `components` object, 它内部 diff 会判断 "配置变了" 然后重新
// 解析 markdown AST. 提到顶层后引用永远稳定, ReactMarkdown 才能跳过重 parse.
//
// 这些 handler 都是**纯函数** (只依赖参数, 没引用外部 state), 提到 module scope
// 完全合规.
const markdownComponents = {
  // 自定义 code 渲染：检测语言 → 路由到对应自定义块
  code({ inline, className, children, ...props }: any) {
    const codeText = String(children).replace(/\n$/, '')

    if (inline) {
      return (
        <code className="aiClerk-md__code-inline" {...props}>
          {children}
        </code>
      )
    }

    // 提取语言：className 形如 "language-photo-list"
    const langMatch = /language-([\w-]+)/.exec(className || '')
    const lang = langMatch?.[1] || ''

    if (CUSTOM_LANG_SET.has(lang)) {
      return (
        <div className="aiClerk-md__custom-block" data-block={lang}>
          {renderCustomBlock(lang as CustomBlockLang, codeText)}
        </div>
      )
    }

    // 普通 code block
    return (
      <pre className="aiClerk-md__code-pre">
        <code className={className} {...props}>
          {children}
        </code>
      </pre>
    )
  },

  // 表格：包一层横向滚动容器，避免在窄会话气泡里被压成"字字换行"
  table({ children, ...props }: any) {
    return (
      <div className="aiClerk-md__table-wrap">
        <table {...props}>{children}</table>
      </div>
    )
  },

  // 链接：新窗口打开
  a({ children, href, ...props }: any) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    )
  }
}

// remarkPlugins 同理 — 数组字面量每次 render 新建会让 ReactMarkdown 内部判断
// "plugin 列表变了" 重新 parse. 提到模块顶层永远稳定.
const markdownRemarkPlugins = [remarkGfm]

// ⚡ React.memo 包裹: 浅比较 props.content (string).
// 同一 content (相同字符串引用) 直接跳过整个 ReactMarkdown 重新 parse + render.
// 主要受益场景: 父 MessageBubble 没被外层 memo 拦住时 (比如 isLastMessage 变化
// 但 content 没变的边界 case), 这里再拦一层.
const MessageContent: React.FC<MessageContentProps> = React.memo(
  ({ content }) => {
    const processed = useMemo(() => preprocessMarkdown(content), [content])
    // 图片预览状态: 点 markdown 内任意 <img> (含表格 cell 里的 ![](url) 缩略图)
    // → 弹大图预览 (ImagePreview 已支持缩放 / 旋转 / 下载 / ESC 关闭).
    // src=null 时 ImagePreview 自身 return null 不渲染, 没开销.
    const [previewSrc, setPreviewSrc] = useState<string | null>(null)
    const closePreview = useCallback(() => setPreviewSrc(null), [])

    // ⚡ Event delegation: outer div 上一个 onClick, 由 e.target 判断点的是不是 img.
    //
    // 为什么不在 ReactMarkdown components 里给 img 加 onClick:
    //   1. 那样 components 里的 img handler 闭包要捕获 setPreviewSrc,
    //      就不能像现在这样把 components 提到 module scope (引用稳定优化失效).
    //   2. 每条消息都要 useCallback + useMemo 包 components, 多一层心智成本.
    //   3. 事件代理对未来扩展 (svg image / picture) 自动生效.
    //
    // iOS 类比: 类似 UIView 的 hitTest 在父 view 上做一次, 而不是每个 subview
    // 都加一个 UITapGestureRecognizer — 同样的 delegation 思想.
    const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement
      if (target.tagName !== 'IMG') return
      // 取真实 src 用 .src (会解析成绝对 URL), 而不是 getAttribute('src') 拿原文
      const src = (target as HTMLImageElement).src
      if (src) setPreviewSrc(src)
    }, [])

    return (
      <div className="aiClerk-md" onClick={handleClick}>
        <ReactMarkdown
          remarkPlugins={markdownRemarkPlugins}
          components={markdownComponents}
        >
          {processed}
        </ReactMarkdown>
        <ImagePreview src={previewSrc} alt="图片预览" onClose={closePreview} />
      </div>
    )
  }
)

MessageContent.displayName = 'MessageContent'

export default MessageContent
